/**
 * Gráfico multi-linha (SVG) — estilo Search Console: cada métrica escala
 * independente (ancorada em zero, não em min-máx — ver comentário em
 * `lineFor`), e passar o mouse mostra uma linha-guia + um cartão com a data
 * e o valor de cada métrica ativa naquele ponto, igual ao comportamento do
 * gráfico de desempenho do Search Console.
 */
import { useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { Metric, MetricDef, MetricKey } from "../lib/metrics";
import { fmtValue } from "../lib/metrics";

interface Props {
  metrics: Metric[]; // ordenados por período (ASC)
  active: MetricKey[];
  defs: MetricDef[];
}

const W = 640;
const H = 220;
const PADX = 10;
const PAD_TOP = 16;
const PAD_BOTTOM = 28;
const INNER_H = H - PAD_TOP - PAD_BOTTOM;

/**
 * `period` é uma data-calendário pura ("2026-07-06"), sem hora — formatar
 * sem `timeZone: "UTC"` faz o fuso local (ex.: Brasil, UTC-3) empurrar a
 * meia-noite UTC pro dia anterior, e a data mostrada fica errada por um
 * dia. Sempre em UTC aqui, pros dois formatos (eixo e tooltip).
 */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC", day: "2-digit", month: "2-digit" });
}
function longDate(iso: string): string {
  const s = new Date(iso).toLocaleDateString("pt-BR", {
    timeZone: "UTC", weekday: "long", day: "numeric", month: "short",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function MultiLineChart({ metrics, active, defs }: Props) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  if (metrics.length === 0) {
    return (
      <p className="type-caption text-text-tertiary">
        Sem dados no período. Registre os números abaixo.
      </p>
    );
  }

  const n = metrics.length;
  const xAt = (i: number): number => (n <= 1 ? W / 2 : PADX + (i * (W - PADX * 2)) / (n - 1));

  // Ancorado em zero (não em min-máx): com poucos registros — o comum aqui,
  // já que os números são digitados à mão — duas métricas que só sobem
  // entre 2 pontos viram a mesma diagonal esticada de canto a canto sob
  // min-máx, não importa a escala real de cada uma. Ancorando em zero, a
  // posição/inclinação reflete o crescimento relativo de verdade.
  function seriesFor(key: MetricKey): (number | null)[] {
    return metrics.map((m) => m[key]);
  }
  function yFor(values: (number | null)[]): (number | null)[] {
    const present = values.filter((v): v is number => v !== null);
    const max = Math.max(...present, 0);
    const range = max || 1;
    return values.map((v) => (v === null ? null : PAD_TOP + (1 - v / range) * INNER_H));
  }
  function pathFor(ys: (number | null)[]): string {
    let d = "";
    ys.forEach((y, i) => {
      if (y === null) return;
      d += `${d === "" ? "M" : "L"}${xAt(i).toFixed(1)},${y.toFixed(1)}`;
    });
    return d;
  }

  const lines = active
    .map((key) => {
      const def = defs.find((d) => d.key === key);
      const values = seriesFor(key);
      if (def === undefined || values.every((v) => v === null)) return null;
      return { def, values, ys: yFor(values) };
    })
    .filter((l): l is { def: MetricDef; values: (number | null)[]; ys: (number | null)[] } => l !== null);

  const labelIdx = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

  function indexAtClientX(clientX: number): number {
    const svg = svgRef.current;
    if (svg === null) return 0;
    const rect = svg.getBoundingClientRect();
    const relX = ((clientX - rect.left) / rect.width) * W;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < n; i++) {
      const dist = Math.abs(xAt(i) - relX);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }
  function onMove(e: ReactPointerEvent<SVGSVGElement>) {
    setHoverIdx(indexAtClientX(e.clientX));
  }
  function onLeave() {
    setHoverIdx(null);
  }

  const hoverRow = hoverIdx === null ? undefined : metrics[hoverIdx];
  const tooltipLeftPct = hoverIdx === null ? 0 : Math.min(88, Math.max(12, (xAt(hoverIdx) / W) * 100));

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full touch-none select-none"
        role="img"
        aria-label="Desempenho ao longo do tempo"
        onPointerMove={onMove}
        onPointerDown={onMove}
        onPointerLeave={onLeave}
      >
        <line x1={PADX} y1={H - PAD_BOTTOM} x2={W - PADX} y2={H - PAD_BOTTOM} stroke="var(--border-subtle)" />

        {hoverIdx !== null && (
          <line
            x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={PAD_TOP} y2={H - PAD_BOTTOM}
            stroke="var(--border-strong)" strokeDasharray="3,3"
          />
        )}

        {lines.map(({ def, ys }) => (
          <g key={def.key}>
            <path d={pathFor(ys)} fill="none" stroke={def.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {ys.map((y, i) => {
              if (y === null) return null;
              const isHover = i === hoverIdx;
              return (
                <circle
                  key={i} cx={xAt(i)} cy={y} r={isHover ? 4.5 : 2.5}
                  fill={def.color} stroke={isHover ? "var(--surface-sunken)" : "none"} strokeWidth="2"
                />
              );
            })}
          </g>
        ))}

        {labelIdx.map((i) => {
          const row = metrics[i];
          if (row === undefined) return null;
          return (
            <text
              key={i} x={xAt(i)} y={H - 8} fontSize="10" fill="var(--text-tertiary)"
              textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
            >
              {shortDate(row.period)}
            </text>
          );
        })}
      </svg>

      {hoverRow !== undefined && lines.length > 0 && (
        <div
          className="liquid-glass pointer-events-none absolute top-1 z-[var(--z-dropdown)] w-60 -translate-x-1/2 rounded-[var(--radius-control)] p-3"
          style={{ left: `${tooltipLeftPct}%` }}
        >
          <p className="type-caption text-text-tertiary">{longDate(hoverRow.period)}</p>
          <div className="mt-2 flex flex-col gap-1.5">
            {lines.map(({ def, values }) => {
              const v = hoverIdx === null ? null : values[hoverIdx] ?? null;
              return (
                <div key={def.key} className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-1.5 type-caption text-text-secondary">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: def.color }} />
                    <span className="truncate">{def.label}</span>
                  </span>
                  <span className="shrink-0 type-caption tabular-nums text-text-cream">
                    {v === null ? "—" : fmtValue(def, v)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
